import React, {
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import Authentication from './pages/authentication'
import './less/App.less'
import 'dayjs/locale/de'
import calendar from 'dayjs/plugin/calendar'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'
import { App as AntdApp, ConfigProvider, Layout } from 'antd'
import { useActiveAntdLocale } from './lib/localeController'
import { useAppThemeConfig } from './lib/themeController'
import 'antd/dist/reset.css'
import * as Sentry from '@sentry/react'
import { initFirebase } from './config/firebaseConfig'
import {
  AnalyticsProvider,
  AuthProvider,
  FirebaseAppProvider,
  FirestoreProvider,
  FunctionsProvider,
  StorageProvider,
  useAnalytics,
  useAuth,
  useFirestore,
} from 'reactfire'
import { logEvent } from 'firebase/analytics'
import { onAuthStateChanged } from 'firebase/auth'
import { disableNetwork, enableNetwork } from 'firebase/firestore'
import { fetchUserCompanyUids } from './lib/useChatwootEnabled'
import { IntercomProvider } from 'react-use-intercom'
import { FeatureFlagsContext } from './lib/FeatureFlagsContext'
import { FEATURE_FLAGS } from './lib/featureFlagKeys'
import { createRoot } from 'react-dom/client'
import styles from './less/BaseLayout.module.less'
import GlobalErrorBoundary from './GlobalErrorBoundary'
import GlobalPageLoading from './components/GlobalPageLoading'
import PageLoading from './components/PageLoading'
import updateLocale from 'dayjs/plugin/updateLocale'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { appVersion } from './lib/general'
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint'
import { BreakpointContext } from './lib/useBreakpointContext'
import './resources/font/satoshi/style.css'
import { OnboardingPage } from './pages/onboarding'
import { OnboardingHelpPage } from './pages/onboardingHelp'
import { QualificationPage } from './pages/qualification'
import { FeatureFlagsProvider } from './lib/FeatureFlagsProvider'
import { FeatureFlags } from './lib/FeatureFlagsContext'
// Importing this module initialises the react-i18next instance.
import { I18nextProvider } from 'react-i18next'
import i18next from './lib/i18n'
import { logger } from './lib/logger'
import { isCompanyFileUploadEvent } from './lib/uploadCompanyFile'
import { getIncompleteKeyExtras } from './lib/firestoreIncompleteKeyContext'

// Initialize Sentry if DSN is provided
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      Sentry.replayIntegration({
        // Privacy settings for session replay
        maskAllText: false, // Show text content by default
        maskAllInputs: true, // Always mask form inputs for security
        blockAllMedia: false, // Show images/videos

        // Specifically mask sensitive elements using CSS selectors
        block: [
          // Cross-origin frame the recorder must not touch — observing it
          // throws SecurityError (CL-WEBAPP-PR).
          'iframe[title="reCAPTCHA"]',
        ],
        mask: [
          // Mask specific elements (shows structure but hides content)
          'input[type="password"]',
          'input[type="email"]',
          '.ant-input-password', // Ant Design password inputs
          // Add more selectors for fields containing PII
        ],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'
    ), // 0.0-1.0 for % of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_REPLAYS_SESSION_SAMPLE_RATE || '0.1'
    ), // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ), // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: appVersion,
    ignoreErrors: [
      'auth/popup-blocked',
      'auth/too-many-requests',
      'auth/network-request-failed',
      // Outlook desktop / corporate "safe links" gateways strip the Referer
      // header on outgoing clicks, which Firebase's referrer-restricted API
      // key rejects. action.jsx already shows a friendly recovery message
      // (copy URL into a browser). Same family as `auth/network-request-failed`.
      // CL-WEBAPP-JG / CL-1311.
      'auth/requests-from-referer-null-are-blocked',
      'auth/popup-closed-by-user',
      'auth/user-cancelled',
      'Connection to Indexed Database server lost',
      "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing",
      // CL-1529 / CL-WEBAPP-Z: WebKit (iOS Safari + Chrome) deletes IndexedDB on data clear,
      // ITP eviction, or private browsing. Firebase throws this internally — not actionable.
      'Database deleted by request of the user',
      'Unauthenticated',
      "'text/html' is not a valid JavaScript MIME type",
      'Missing or insufficient permissions',
      // Microsoft Office.js / Outlook safelinks scanner content scripts
      // throw this when their internal RPC bridge can't find a registered
      // handler on a non-Office page. Third-party extension noise — not
      // anything our app does or can fix. CL-1170 / CL-WEBAPP-2Z.
      'Object Not Found Matching Id',
    ],
    beforeSend(event) {
      const exc = event.exception?.values?.[0]
      if (!exc) return event

      const exceptionValue = exc.value ?? ''
      const stackFrames = exc.stacktrace?.frames ?? []
      const hasFirebaseModule = stackFrames.some(
        (f) =>
          f.module?.includes('@firebase') ||
          f.filename?.includes('@firebase') ||
          f.abs_path?.includes('@firebase')
      )

      // Some Android WebViews (seen on Zebra enterprise handhelds) inject helper
      // scripts via evaluateJavascript() for autofill and forced night-mode CSS.
      // Those injected scripts assume a browser-bridge global (`xbrowser`,
      // `swbrowser`, ...) that isn't present in a standard WebView, so they
      // throw `ReferenceError: <x>browser is not defined` from injected
      // `<anonymous>` frames (function names like `execute_auto_fill` /
      // `needInjectCss`). window.onerror captures it as if it were ours.
      // Session replays show the crash can leave the shell's night-mode repaint
      // half-applied and visually break the page on that device, but it's
      // injected third-party code — not our bundle (no first-party frames) and
      // not fixable from our side, so we drop it. Match the whole `*browser`
      // family so future variants drop into the same guard; no first-party
      // code references a global of this shape.
      // CL-1875 / CL-WEBAPP-M2 / CL-WEBAPP-NW.
      if (
        exc.type === 'ReferenceError' &&
        /^[\w$]*browser is not defined$/.test(exceptionValue)
      ) {
        return null
      }

      if (
        exc.type === 'RangeError' &&
        exceptionValue.startsWith('Maximum call stack size exceeded') &&
        /CriOS|GSA/.test(navigator.userAgent) &&
        stackFrames.length > 0 &&
        !stackFrames.some(
          (f) =>
            (f.filename ?? '').includes('://') ||
            (f.abs_path ?? '').includes('://')
        )
      ) {
        return null
      }

      // The api() wrapper records the dispatcher method as an `api.error`
      // breadcrumb (src/lib/api.ts) at throw time. Promote the most recent one
      // to a tag so callable failures are attributable to a method in the
      // Sentry UI instead of collapsing into one opaque per-message group.
      // CL-WEBAPP-H2.
      const apiMethod = (event.breadcrumbs ?? [])
        .filter(
          (b) =>
            b.category === 'api.error' &&
            typeof b.data?.method === 'string' &&
            b.data.method.length > 0
        )
        .map((b) => b.data.method)
        .pop()
      if (apiMethod) {
        event.tags = { ...(event.tags ?? {}), apiMethod }
      }

      if (
        exceptionValue.startsWith(
          'Failed to fetch dynamically imported module'
        ) ||
        // Safari / WebKit emits this when a dynamic import response can't be
        // parsed as a module (typical post-deploy stale-chunk case). The
        // matching auto-reload lives in GlobalErrorBoundary. CL-WEBAPP-A.
        exceptionValue.startsWith('Importing a module script failed') ||
        // Vite's preload helper throws this when a code-split CSS chunk's
        // <link> 404s — same post-deploy stale-asset family as above. The
        // matching auto-reload lives in the `vite:preloadError` listener
        // below. CL-WEBAPP-MC.
        exceptionValue.startsWith('Unable to preload CSS for')
      ) {
        return null
      }

      // Same post-deploy stale-chunk family: on Safari a failed dynamic import
      // can resolve to `undefined`, so React's lazy initializer throws a
      // TypeError reading `_result.default` instead of the reject-path messages
      // above. Chrome/V8 surfaces the identical crash but names only the
      // property read ("Cannot read properties of undefined (reading
      // 'default')"). The matching auto-reload lives in GlobalErrorBoundary.
      // CL-WEBAPP-ME / CL-WEBAPP-MF.
      if (
        exc.type === 'TypeError' &&
        (exceptionValue.includes('_result.default') ||
          exceptionValue ===
            "Cannot read properties of undefined (reading 'default')")
      ) {
        return null
      }

      const framelessNoise = new Set([
        'Load failed',
        'Failed to fetch',
        'Non-Error promise rejection captured with value: Timeout',
      ])
      if (stackFrames.length === 0 && framelessNoise.has(exceptionValue)) {
        return null
      }

      // iOS Safari aborts in-flight requests (fetch, Firebase SDK, analytics)
      // when the tab is backgrounded or the user navigates away, surfacing as
      // an unhandled `AbortError` (DOMException code 20) with no in-app frames.
      // Our own abortable call sites all catch AbortError, so a frameless one
      // is cancellation noise, not a bug. Guard on no-frames so a genuine
      // in-code AbortError (with a real stack) still reports. CL-WEBAPP-4A.
      if (
        stackFrames.length === 0 &&
        (exc.type === 'AbortError' || /AbortError/.test(exceptionValue))
      ) {
        return null
      }

      // A script in document.head failing (extension injection, blockers, bad network)
      // can reject a promise with a DOM ErrorEvent. Sentry stringifies it with no stack.
      // CL-WEBAPP-G2.
      if (
        stackFrames.length === 0 &&
        /`Event` \(type=error\) captured as promise rejection/.test(
          exceptionValue
        )
      ) {
        return null
      }

      // Generalised to any `firebase*.googleapis.com` subdomain so future
      // Firebase services drop into the same family without bespoke entries.
      // Currently catches `firebase.googleapis.com` (Auth, Firestore, etc.)
      // and `firebaseinstallations.googleapis.com` (CL-1217 / CL-WEBAPP-FH).
      if (
        exceptionValue.includes('remoteconfig/fetch-client-network') ||
        /firebase[a-z]*\.googleapis\.com/.test(exceptionValue)
      ) {
        return null
      }

      // Firebase Installations registers the app instance at SDK init (it
      // underpins Analytics, FCM, Remote Config and App Check). Google's
      // backend intermittently returns 503/transient failures, surfaced as
      // `installations/request-failed`. This variant's message carries only the
      // code and a 503 description (no googleapis URL), so the filter above
      // misses it. Background telemetry: the SDK retries and the page renders,
      // so there is no user impact. Same family as CL-1217 / CL-1158.
      // CL-WEBAPP-MM.
      if (exceptionValue.includes('installations/request-failed')) {
        return null
      }

      // Firebase Storage exhausts its retry budget on slow / unstable networks
      // (e.g. mobile data in low-bandwidth regions). The SDK retries with
      // exponential backoff and throws this when the window is up. It's an
      // environmental condition, not a code bug — the user is shown a
      // friendly retry message in `errorResponse`. CL-WEBAPP-HQ.
      if (
        exceptionValue.includes('storage/retry-limit-exceeded') &&
        !isCompanyFileUploadEvent(event)
      ) {
        return null
      }

      if (exceptionValue === 'Load failed') {
        if (hasFirebaseModule) return null

        // In production builds, source-mapped paths aren't available in beforeSend
        // (source maps are resolved server-side), so hasFirebaseModule is always false.
        // Fall back to breadcrumbs: the Sentry SDK records failed fetch URLs before
        // this callback runs, so a failed request to firebase.googleapis.com proves
        // this is Firebase SDK noise, not an application error.
        const hasFailedFirebaseFetch = (event.breadcrumbs ?? []).some((b) => {
          if (
            b.category !== 'fetch' ||
            typeof b.data?.url !== 'string' ||
            !b.data.url.includes('firebase.googleapis.com')
          )
            return false
          const status = Number(b.data?.status_code)
          return (
            (b.level === 'error' && !Number.isFinite(status)) ||
            (Number.isFinite(status) && status >= 400)
          )
        })
        if (hasFailedFirebaseFetch) return null
      }

      // Firebase App Check's reCAPTCHA provider occasionally times out during
      // background token refresh (slow network, throttled tab, content blockers).
      // The SDK retries automatically — no user impact. Google's library
      // appends a timer-variable name (e.g. "reCAPTCHA Timeout (b)"), so match
      // by prefix.
      if (exceptionValue.startsWith('reCAPTCHA Timeout')) {
        return null
      }

      // Match the frame, never the message: reCAPTCHA's minified property
      // names are renamed on each Google release, so a message match silently
      // spawns a new issue per rollout. The crash site is the last frame
      // (stripSentryFramesAndReverse), so anything reCAPTCHA raises inside one
      // of our own callbacks still reports.
      const crashFrame = stackFrames[stackFrames.length - 1]
      if (
        crashFrame &&
        /\/recaptcha\/[^?\s]*\.js/.test(
          crashFrame.abs_path ?? crashFrame.filename ?? ''
        )
      ) {
        return null
      }

      // Firebase Analytics' fetchDynamicConfig hits a Google API at SDK init.
      // When the browser doesn't send a Referer (Outlook safelinks, direct
      // navigation, privacy extensions), GCP API key referrer restrictions
      // return 403 with `analytics/config-fetch-failed`. The SDK throws this
      // as an unhandled rejection. Pure telemetry — page renders normally,
      // user notices nothing. Same family as CL-1158 (Installations).
      //
      // After repeated config-fetch failures the SDK enters exponential
      // backoff and rejects subsequent calls with `analytics/fetch-throttle` —
      // the same root cause, just the follow-up error.
      if (
        exceptionValue.includes('analytics/config-fetch-failed') ||
        exceptionValue.includes('analytics/fetch-throttle')
      ) {
        return null
      }

      // The @firebase/functions client SDK falls back to
      // `code: 'internal', message: 'internal'` when fetch returns no
      // parseable response — typically Mobile Safari aborting a
      // backgrounded request before it completes. Bare 'internal' has no
      // descriptive content, no in-app frames, and no server-side trace
      // to correlate — zero diagnostic value. Real backend errors still
      // surface via the backend Sentry project (captureError in
      // functions/src/functions/api.ts) and Cloud Run logs.
      // CL-WEBAPP-GK / CL-1218.
      if (
        exc.type === 'FirebaseError' &&
        exceptionValue === 'internal' &&
        !isCompanyFileUploadEvent(event)
      ) {
        return null
      }

      // FirebaseError from the @firebase/functions client SDK has the same
      // constructor stack regardless of what the server returned, so Sentry's
      // default stack-based grouping buckets unrelated callable failures
      // (password reset, billing, travel expense, etc.) into one issue.
      // Force per-message fingerprinting so each distinct server error gets
      // its own group.
      if (exc.type === 'FirebaseError' && exceptionValue) {
        // Include the callable method (when known) so generic codes like
        // deadline-exceeded split per-method into actionable issues instead of
        // one catch-all group. Note: bare `internal` is dropped above and never
        // reaches here, so it isn't split. CL-WEBAPP-H2.
        //
        // Exception: the Firestore SDK watch-stream families handled by the
        // ErrorBoundary auto-reload never originate from a callable, so a
        // leftover `api.error` breadcrumb from an unrelated earlier call must
        // not split them into new per-method issues. Pin them to their
        // canonical message-only group. CL-1870 / CL-WEBAPP-NN.
        const isFirestoreSdkFamily =
          exceptionValue === 'incomplete key.' ||
          exceptionValue.startsWith('Target ID already exists')
        // deadline-exceeded is the client SDK's ~70s give-up timer firing —
        // almost always mobile network flakiness, not a property of the
        // method (90d of per-method issues were all 1-2-event singletons).
        // The per-method split just spawns a new Sentry issue per page, so
        // pin it to one message-only group; the apiMethod tag keeps the
        // per-method breakdown inside that issue, and genuinely slow
        // backends show in Cloud Run latency, not here. CL-1873 /
        // CL-WEBAPP-NT.
        const isDeadlineExceeded = exceptionValue === 'deadline-exceeded'
        event.fingerprint =
          apiMethod && !isFirestoreSdkFamily && !isDeadlineExceeded
            ? ['firebase-error', exceptionValue, apiMethod]
            : ['firebase-error', exceptionValue]
      }

      // CL-WEBAPP-GH: server-side watch-target rejection on iOS during
      // post-login bootstrap. Enrich with URL/auth-timing context so future
      // events narrow which listener path triggers it. CL-1303.
      if (
        exc.type === 'FirebaseError' &&
        exceptionValue === 'incomplete key.'
      ) {
        event.tags = { ...(event.tags ?? {}), firestore_incomplete_key: 'true' }
        event.extra = { ...(event.extra ?? {}), ...getIncompleteKeyExtras() }
      }

      return event
    },
  })
  logger.info(
    'Sentry initialized for environment: ' +
      import.meta.env.VITE_SENTRY_ENVIRONMENT
  )
} else {
  logger.info('Sentry disabled - no DSN provided')
}

// Vite dispatches `vite:preloadError` when a code-split chunk (JS or CSS)
// fails to preload — the classic post-deploy stale-asset case where the
// already-loaded index.html references hashed files the new build no longer
// serves. Reload once to pull the fresh bundle so the user auto-recovers
// instead of hitting a broken page. Loop-guarded via sessionStorage, mirroring
// the chunk-load reload in GlobalErrorBoundary. The residual Sentry event is
// dropped in beforeSend ('Unable to preload CSS for ...'). CL-WEBAPP-MC.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    // `vite:preloadError` is cancelable; without preventDefault Vite rethrows
    // the original preload error after dispatch. Cancel it so the error never
    // reaches window.onerror / the console while the reload takes over.
    event.preventDefault()
    const RELOAD_KEY = 'vite-preload-error-reload'
    const lastReload = sessionStorage.getItem(RELOAD_KEY)
    const now = Date.now()
    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(now))
      window.location.reload()
    }
  })
}

// antd's static message / notification / Modal.confirm render outside the
// React tree and so outside the root ConfigProvider. Give their holder the same
// theme and locale, otherwise they stay light in dark mode.
// eslint-disable-next-line react-refresh/only-export-components
function StaticHolder({ children }) {
  return (
    <ConfigProvider locale={useActiveAntdLocale()} theme={useAppThemeConfig()}>
      {children}
    </ConfigProvider>
  )
}
ConfigProvider.config({
  holderRender: (children) => <StaticHolder>{children}</StaticHolder>,
})

dayjs.extend(calendar)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.extend(localizedFormat)
dayjs.locale('de')

dayjs.updateLocale('de', {
  calendar: {
    lastDay: '[Gestern um] LT',
    sameDay: '[Heute um] LT',
    nextDay: '[Morgen um] LT',
    lastWeek: '[Letzten] dddd [um] LT',
    nextWeek: 'dddd [um] LT',
    sameElse: 'L [um] LT',
  },
})

export const PageLoadingContext = React.createContext([
  false,
  (loading) => loading,
])

const firebaseInitPromise = initFirebase()

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID

// Validate Intercom App ID to prevent silent failures
if (!INTERCOM_APP_ID || INTERCOM_APP_ID.trim() === '') {
  // eslint-disable-next-line no-console
  console.warn(
    '[Cleverlohn] VITE_INTERCOM_APP_ID environment variable is not set. Intercom integration will not work. ' +
      'Please set this variable in your environment configuration.'
  )
}

// eslint-disable-next-line react-refresh/only-export-components
function Render({
  firebaseApp,
  firestore,
  functions,
  storage,
  auth,
  featureFlags,
  analytics,
}) {
  const [pageLoading, setPageLoading] = useState(false)
  const breakpoint = useBreakpoint()
  const [breakpointValue, setBreakpointValue] = useState(breakpoint)
  useLayoutEffect(() => {
    setBreakpointValue(breakpoint)
  }, [breakpoint])

  // Workaround for a known Firebase JS SDK + mobile Safari/iOS interaction:
  // when a tab is suspended in the background, the Firestore listen stream's
  // resume token state can be corrupted. On reconnect the server rejects it
  // with `[code=invalid-argument]: incomplete key.` (CL-WEBAPP-GH).
  //
  // Cleanly close the Firestore network when the tab is hidden and re-open
  // it on resume so there is no stale resume state for the SDK to retry.
  // Refs:
  //   - https://github.com/firebase/firebase-js-sdk/issues/3495
  //   - https://github.com/firebase/firebase-js-sdk/issues/2581
  //
  // CL-1296: only re-enable the network if we previously disabled it. The
  // earlier version called `enableNetwork(firestore)` unconditionally on
  // mount for foreground tabs, which raced with first-listener target
  // allocation in the Firestore SDK and produced `Target ID already exists`
  // errors on routes that subscribe to a doc during initial render.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    let networkDisabledByUs = false

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (networkDisabledByUs) return
        networkDisabledByUs = true
        disableNetwork(firestore).catch(() => {
          networkDisabledByUs = false
        })
      } else if (document.visibilityState === 'visible') {
        if (!networkDisabledByUs) return
        networkDisabledByUs = false
        enableNetwork(firestore).catch(() => {})
      }
    }

    // Only act on mount if the tab is already hidden (session restore,
    // Cmd-click, background window.open). For a visible tab the network is
    // already enabled, so calling enableNetwork here was a no-op that still
    // raced with listener registration; skip it.
    if (document.visibilityState === 'hidden') {
      onVisibilityChange()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [firestore])

  const antdLocale = useActiveAntdLocale()
  const appTheme = useAppThemeConfig()

  return (
    <GlobalErrorBoundary auth={auth}>
      <I18nextProvider i18n={i18next}>
        <ConfigProvider locale={antdLocale} theme={appTheme}>
          {/* antd's App provider supplies the message/notification/modal
                context that App.useApp() reads. `component={false}` renders no
                wrapping DOM node, so it has no layout impact. Without it,
                App.useApp() returns antd's empty default context and calls like
                message.error() throw "message.error is not a function"
                (CL-1574 / CL-WEBAPP-KY). */}
          <AntdApp component={false}>
            <BreakpointContext.Provider value={breakpointValue}>
              <FirebaseAppProvider firebaseApp={firebaseApp} suspense>
                <AuthProvider sdk={auth}>
                  <OptionalAnalyticsProvider sdk={analytics}>
                    <FirestoreProvider sdk={firestore}>
                      <StorageProvider sdk={storage}>
                        <FeatureFlagsProvider featureFlags={featureFlags}>
                          <FunctionsProvider sdk={functions}>
                            <OptionalIntercomProvider appId={INTERCOM_APP_ID}>
                              <PageLoadingContext.Provider
                                value={[pageLoading, setPageLoading]}
                              >
                                <Layout className={styles.baseLayout}>
                                  <GlobalPageLoading
                                    pageLoading={pageLoading}
                                  />
                                  <Suspense fallback={<PageLoading />}>
                                    <BrowserRouter>
                                      <Routes>
                                        <Route
                                          path="/onboarding/:companyUid/help/*"
                                          element={<OnboardingHelpPage />}
                                        />
                                        <Route
                                          path="/onboarding/:companyUid/*"
                                          element={<OnboardingPage />}
                                        />
                                        <Route
                                          path="/qualification"
                                          element={<QualificationPage />}
                                        />
                                        <Route
                                          path="/*"
                                          element={<Authentication />}
                                        />
                                      </Routes>
                                      <AnalyticsRouteLogger />
                                    </BrowserRouter>
                                  </Suspense>
                                </Layout>
                              </PageLoadingContext.Provider>
                            </OptionalIntercomProvider>
                          </FunctionsProvider>
                        </FeatureFlagsProvider>
                      </StorageProvider>
                    </FirestoreProvider>
                  </OptionalAnalyticsProvider>
                </AuthProvider>
              </FirebaseAppProvider>
            </BreakpointContext.Provider>
          </AntdApp>
        </ConfigProvider>
      </I18nextProvider>
    </GlobalErrorBoundary>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
function AnalyticsRouteLogger() {
  if (import.meta.env.MODE === 'development') {
    return null
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const location = useLocation()
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const analytics = useAnalytics()

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      logEvent(analytics, 'page_view', {
        href: location.pathname,
      })
    }, [location.pathname, analytics])
  }
}

// eslint-disable-next-line react-refresh/only-export-components
function OptionalAnalyticsProvider({ sdk, children }) {
  if (sdk) {
    return <AnalyticsProvider sdk={sdk}>{children}</AnalyticsProvider>
  }
  return children
}

// eslint-disable-next-line react-refresh/only-export-components
function OptionalIntercomProvider({ appId, children }) {
  // When Chatwoot is enabled we skip Intercom entirely so its script/launcher
  // never gets injected alongside Chatwoot. We read the flag via the context
  // directly (rather than useFeatureFlag) because this component is mounted
  // outside <BrowserRouter>, and useFeatureFlag depends on useLocation.
  const featureFlags = useContext(FeatureFlagsContext)
  const auth = useAuth()
  const firestore = useFirestore()

  // Wait for Remote Config to finish activating before deciding which widget
  // to mount. Otherwise the SDK default (false) flips later to true once
  // Remote Config resolves, and by then the Intercom <script> has already
  // been injected into the DOM — unmounting <IntercomProvider> does NOT
  // remove the messenger iframe/launcher.
  const [flagsReady, setFlagsReady] = useState(!featureFlags)
  useEffect(() => {
    if (!featureFlags) {
      setFlagsReady(true)
      return
    }
    let cancelled = false
    void featureFlags.ready.finally(() => {
      if (!cancelled) setFlagsReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [featureFlags])

  // Resolve the signed-in user's company memberships BEFORE hooking Intercom in
  // at all (CL-1790). Merely mounting <IntercomProvider> injects the widget
  // script, which self-starts a visitor-mode messenger off the pre-set
  // `window.intercomSettings.app_id` — no boot() involved — and that launcher
  // cannot be reliably removed afterwards. So Intercom stays unmounted until the
  // allowlist decision is final. This component sits outside <BrowserRouter>, so
  // it can't reuse useChatwootEnabledForUserState (which needs useLocation);
  // it shares fetchUserCompanyUids with that hook instead.
  const [membership, setMembership] = useState({
    companyUids: [],
    loading: true,
  })
  useEffect(() => {
    let cancelled = false
    let generation = 0
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const current = ++generation
      if (!user) {
        // Signed-out users belong to no company.
        if (!cancelled) setMembership({ companyUids: [], loading: false })
        return
      }
      // Clear stale membership while the new user's queries run.
      setMembership({ companyUids: [], loading: true })
      fetchUserCompanyUids(firestore, user.uid)
        .then((companyUids) => {
          if (cancelled || current !== generation) return
          setMembership({ companyUids, loading: false })
        })
        .catch(() => {
          // Fail closed: an unreadable query mounts Intercom as before rather
          // than blocking support chat entirely.
          if (cancelled || current !== generation) return
          setMembership({ companyUids: [], loading: false })
        })
    })
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [auth, firestore])

  const enableChatwoot =
    featureFlags?.getCompanyAudienceFlag(
      FEATURE_FLAGS.CHATWOOT_COMPANIES_ENABLED,
      membership.companyUids
    ) ?? false

  // Decision not final yet: keep Intercom unhooked. useSafeIntercom's fallback
  // covers consumers with noops until the provider mounts (if it ever does).
  if (!flagsReady || membership.loading) {
    return children
  }

  if (enableChatwoot) {
    return children
  }

  if (!appId || appId.trim() === '') {
    return children
  }

  // Pre-initialize window.intercomSettings with app_id BEFORE IntercomProvider mounts.
  // This prevents a race condition where Intercom's script loads and checks for app_id
  // before our lazy-loaded pages call boot(). The IntercomProvider will merge additional
  // settings when boot() is called later.
  if (typeof window !== 'undefined' && !window.intercomSettings?.app_id) {
    window.intercomSettings = { app_id: appId }
  }

  return (
    <IntercomProvider appId={appId} autoBoot={false}>
      {children}
    </IntercomProvider>
  )
}

const container = document.getElementById('root')

const root = createRoot(container)

const start = async () => {
  const {
    firebaseApp,
    firestore,
    functions,
    storage,
    auth,
    remoteConfig,
    analytics,
  } = await firebaseInitPromise

  const featureFlags = new FeatureFlags(remoteConfig ?? null)

  if (import.meta.env.MODE === 'development') {
    window.onEmulatorsEvaluated((props) => {
      root.render(
        <Render
          {...props}
          firebaseApp={firebaseApp}
          featureFlags={featureFlags}
          analytics={analytics}
          local={true}
        />
      )
    })
  } else {
    root.render(
      <Render
        firebaseApp={firebaseApp}
        firestore={firestore}
        functions={functions}
        storage={storage}
        auth={auth}
        featureFlags={featureFlags}
        analytics={analytics}
      />
    )
  }
}

start().catch((error) => {
  logger.captureException(error)
})
